import React, { useState, useEffect } from 'react';
import Layout from '../Layout';
import { getSchedulerConfig, updateSchedulerConfig } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

const DAYS = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

const TIMEZONES = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Madrid',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
];

function SchedulerConfig() {
    const [config, setConfig] = useState(null);
    const [form, setForm] = useState({ enabled: true, day_of_week: 1, hour: 0, minute: 0, timezone: 'UTC' });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getSchedulerConfig();
            setConfig(res.data);
            setForm({
                enabled: res.data.enabled,
                day_of_week: res.data.day_of_week,
                hour: res.data.hour,
                minute: res.data.minute,
                timezone: res.data.timezone,
            });
        } catch (err) {
            setError('Failed to load scheduler config: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await updateSchedulerConfig(form);
            setConfig(res.data);
            setSuccess('Configuration saved and scheduler restarted successfully.');
        } catch (err) {
            setError('Failed to save config: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const getDayLabel = (v) => DAYS.find(d => d.value === v)?.label || '-';
    const formatTime = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

    return (
        <Layout>
            <div className="container py-4" style={{ maxWidth: 860 }}>
                <div className="mb-4">
                    <h4 className="fw-bold mb-1">
                        <i className="bi bi-calendar-check me-2"></i>Scheduler Config
                    </h4>
                    <p className="text-muted mb-0">
                        Configure when the automatic data fetch runs for all enabled baskets.
                    </p>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                {loading ? (
                    <div className="text-center py-5">
                        <span className="spinner-border text-primary" />
                    </div>
                ) : (
                    <div className="row g-4">
                        <div className="col-md-7">
                            <div className="card shadow-sm">
                                <div className="card-header fw-semibold">
                                    <i className="bi bi-sliders me-2"></i>Configuration
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSave}>
                                        <div className="mb-4">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="enabledSwitch"
                                                    checked={form.enabled}
                                                    onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                                                />
                                                <label className="form-check-label fw-semibold" htmlFor="enabledSwitch">
                                                    {form.enabled
                                                        ? <span className="text-success"><i className="bi bi-check-circle me-1"></i>Scheduler Enabled</span>
                                                        : <span className="text-secondary"><i className="bi bi-pause-circle me-1"></i>Scheduler Disabled</span>
                                                    }
                                                </label>
                                            </div>
                                            <small className="text-muted">
                                                When disabled, no automatic fetch will run. Manual fetch is still available.
                                            </small>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Day of Week</label>
                                            <select
                                                className="form-select"
                                                value={form.day_of_week}
                                                onChange={e => setForm(f => ({ ...f, day_of_week: parseInt(e.target.value, 10) }))}
                                            >
                                                {DAYS.map(d => (
                                                    <option key={d.value} value={d.value}>{d.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="row g-3 mb-3">
                                            <div className="col-6">
                                                <label className="form-label fw-semibold">Hour (0-23)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min={0} max={23}
                                                    value={form.hour}
                                                    onChange={e => setForm(f => ({ ...f, hour: parseInt(e.target.value, 10) || 0 }))}
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label fw-semibold">Minute (0-59)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min={0} max={59}
                                                    value={form.minute}
                                                    onChange={e => setForm(f => ({ ...f, minute: parseInt(e.target.value, 10) || 0 }))}
                                                />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="form-label fw-semibold">Timezone</label>
                                            <select
                                                className="form-select"
                                                value={form.timezone}
                                                onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}
                                            >
                                                {TIMEZONES.map(tz => (
                                                    <option key={tz} value={tz}>{tz}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <button className="btn btn-primary w-100" type="submit" disabled={saving}>
                                            {saving
                                                ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</>
                                                : <><i className="bi bi-check2-circle me-2"></i>Save Configuration</>
                                            }
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-5">
                            <div className="card shadow-sm h-100">
                                <div className="card-header fw-semibold">
                                    <i className="bi bi-info-circle me-2"></i>Current Config Summary
                                </div>
                                <div className="card-body">
                                    {config ? (
                                        <ul className="list-group list-group-flush">
                                            <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                <span className="text-muted">Status</span>
                                                {config.enabled
                                                    ? <span className="badge bg-success">Enabled</span>
                                                    : <span className="badge bg-secondary">Disabled</span>
                                                }
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between px-0">
                                                <span className="text-muted">Day</span>
                                                <strong>{getDayLabel(config.day_of_week)}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between px-0">
                                                <span className="text-muted">Time</span>
                                                <strong>{formatTime(config.hour, config.minute)}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between px-0">
                                                <span className="text-muted">Timezone</span>
                                                <strong>{config.timezone}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between px-0">
                                                <span className="text-muted">Last updated</span>
                                                <small>{(config.updatedAt || config.updated_at) ? new Date(config.updatedAt || config.updated_at).toLocaleString() : '-'}</small>
                                            </li>
                                        </ul>
                                    ) : (
                                        <p className="text-muted">No config loaded.</p>
                                    )}

                                    <div className="mt-4 p-3 bg-light rounded">
                                        <p className="mb-1 small text-muted fw-semibold">
                                            <i className="bi bi-clock me-1"></i>Scheduled run
                                        </p>
                                        {config && config.enabled ? (
                                            <p className="mb-0 fw-bold text-primary">
                                                Every {getDayLabel(config.day_of_week)} at {formatTime(config.hour, config.minute)} ({config.timezone})
                                            </p>
                                        ) : (
                                            <p className="mb-0 text-secondary fst-italic">Scheduler is disabled.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default SchedulerConfig;
